import type { CardSizeId } from './layout'
import {
  CARD_SIZES,
  cardLayout,
  FONT_DISPLAY,
  FONT_UI,
  WEIGHT_HERO,
  WEIGHT_LABEL,
} from './layout'
import type { CardModel } from './model'
import { paintCard } from './paint'

/**
 * Getting a painted card out of the browser and into a file.
 *
 * The DOM half of the card, deliberately separate from `paint.ts` so that
 * everything decidable stays testable in node and only this — canvas creation,
 * font loading, PNG encoding — needs a real browser.
 */

/**
 * The (weight, family) pairs the card actually draws with. `document.fonts.load`
 * takes CSS font shorthand and will happily resolve a face that is never used, so
 * this list is kept in step with `paint.ts` by hand rather than derived; there are
 * only two faces and adding a third is not a thing that happens quietly.
 *
 * The size in each shorthand is arbitrary — both faces are variable and cover
 * their whole weight range from one file — but `document.fonts.check` only
 * answers truthfully for a shorthand it has been asked to load, so the same
 * strings are used for both calls.
 */
const FONT_SPECS = [
  `${WEIGHT_HERO} 100px ${FONT_DISPLAY}`,
  `${WEIGHT_LABEL} 100px ${FONT_DISPLAY}`,
  `${WEIGHT_LABEL} 100px ${FONT_UI}`,
] as const

/**
 * A sample of the glyphs the card draws, passed to `document.fonts.load` as its
 * text argument.
 *
 * That argument matters more than it looks: the faces are latin-subsetted with an
 * explicit `unicode-range`, and the browser matches the requested text against it
 * to decide which faces are actually needed. Omitting the text works, but asking
 * with a representative sample is what makes the answer meaningful.
 */
const SAMPLE_GLYPHS = 'Glidepath 0123456789% —.'

/**
 * Waits for the card's faces, but never longer than `timeoutMs`.
 *
 * `font-display: swap` means a share tapped in the first moments after a cold
 * load would otherwise rasterise in the system fallback and produce a card that
 * does not look like the product. Equally, a font that never arrives must not
 * wedge the share button — so this resolves either way and reports which
 * happened. Drawing in the fallback is a worse card; hanging is a broken feature.
 */
let fontsReady: Promise<boolean> | null = null

export function ensureCardFonts(timeoutMs = 1500): Promise<boolean> {
  /*
   * Memoised on SUCCESS only.
   *
   * A share renders two cards concurrently and the idle warm may still be in
   * flight, so without a memo this ran three overlapping 1500ms races for the
   * same three faces. But "once loaded they stay loaded" is true of `true` and
   * false of `false`: caching a failure meant a warm that lost its race on a
   * slow connection answered `false` instantly and permanently, so a share
   * tapped moments later skipped the wait entirely and rasterised in the system
   * fallback — the exact outcome this function exists to prevent, on exactly the
   * in-app-browser traffic that makes it likely.
   */
  fontsReady ??= loadCardFonts(timeoutMs).then((ok) => {
    if (!ok) fontsReady = null
    return ok
  })
  return fontsReady
}

async function loadCardFonts(timeoutMs: number): Promise<boolean> {
  if (typeof document === 'undefined' || !('fonts' in document)) return false

  const loads = FONT_SPECS.map((spec) =>
    document.fonts.load(spec, SAMPLE_GLYPHS).catch(() => []),
  )

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs)
  })

  try {
    await Promise.race([Promise.all(loads), timeout])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  return FONT_SPECS.every((spec) => document.fonts.check(spec, SAMPLE_GLYPHS))
}

/**
 * Paints a card and encodes it as a PNG.
 *
 * No `devicePixelRatio` anywhere: card dimensions are absolute output pixels, not
 * CSS pixels. A 1080x1350 post is 1080x1350 on every device, and multiplying by
 * DPR would produce a 3x file that Instagram would only downscale again.
 *
 * At the largest size (1080x1920) this is 2.07M pixels, comfortably inside iOS
 * Safari's canvas area limit.
 */
export async function renderCardToBlob(
  model: CardModel,
  sizeId: CardSizeId,
): Promise<Blob> {
  // Deliberately not fatal when this resolves false. A card set in the system
  // fallback is worse than one in Space Grotesk; a share button that refuses to
  // do anything because a font stalled is worse than both.
  await ensureCardFonts()

  const { width, height } = CARD_SIZES[sizeId]
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('Could not get a 2D context to draw the card')

  paintCard(ctx, model, cardLayout(sizeId))

  try {
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob === null) reject(new Error('The card could not be encoded as a PNG'))
        else resolve(blob)
      }, 'image/png')
    })
  } finally {
    // Release the backing store rather than waiting for GC; on a phone these are
    // several megabytes each and two are rendered per share.
    canvas.width = 0
    canvas.height = 0
  }
}
